import React from 'react';
import { useTranslation } from 'react-i18next';

// Showcase of regional vehicle categories. Replace image paths with real photos placed under /public/images/cars/
// Example expected files (user to provide):
// /public/images/cars/japanese.jpg
// /public/images/cars/korean.jpg
// /public/images/cars/american.jpg
// /public/images/cars/european.jpg
// Each card gracefully falls back to a gradient if the image is missing.

export default function VehicleOrigins() {
  const { t } = useTranslation();
  
  const origins = [
    {
      key: 'japanese',
      label: t('origins.japanese'),
      img: '/images/cars/japanese.jpg',
      points: [
        t('origins.japanese.reliability'),
        t('origins.japanese.engineering'),
        t('origins.japanese.performance')
      ]
    },
    {
      key: 'korean',
      label: t('origins.korean'),
      img: '/images/cars/korean.webp',
      points: [
        t('origins.korean.innovation'),
        t('origins.korean.tech'),
        t('origins.korean.warranty')
      ]
    },
    {
      key: 'american',
      label: t('origins.american'),
      img: '/images/cars/american.jpg',
      points: [
        t('origins.american.heritage'),
        t('origins.american.torque'),
        t('origins.american.design')
      ]
    },
    {
      key: 'european',
      label: t('origins.european'),
      img: '/images/cars/european.jpg',
      points: [
        t('origins.european.dynamics'),
        t('origins.european.luxury'),
        t('origins.european.performance')
      ]
    }
  ];
  
  return (
    <section className="py-20 bg-white" id="origins">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">{t('origins.title')}</h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">{t('origins.subtitle')}</p>
        </div>
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {origins.map((o, i) => (
            <div
              key={o.key}
              className="group relative rounded-3xl overflow-hidden h-72 flex flex-col justify-end shadow-[0_4px_20px_-4px_rgba(0,0,0,0.35)] ring-1 ring-blue-200/60 bg-blue-950/70 backdrop-blur-sm transition-all duration-500 hover:shadow-[0_10px_30px_-6px_rgba(0,0,0,0.55)] hover:ring-[#F5D313]/60 hover:-translate-y-1"
              style={{ animation: `fadeInUp 0.7s ease ${(i * 90)}ms both` }}
            >
              <div className="absolute inset-0 overflow-hidden">
                <img
                  src={o.img}
                  alt={o.label}
                  className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-[2200ms] ease-[cubic-bezier(.19,1,.22,1)]"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-950 via-blue-900/70 to-blue-900/0" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 mix-blend-overlay bg-[radial-gradient(circle_at_70%_30%,rgba(245,211,19,0.15),transparent_60%)]" />
              </div>
              <div className="relative p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center justify-center h-6 px-3 rounded-full bg-[#F5D313]/20 text-[#F5D313] text-[10px] font-semibold tracking-wide uppercase shadow-inner border border-[#F5D313]/30 backdrop-blur-sm">
                    {o.key}
                  </span>
                </div>
                <h3 className="font-semibold text-white text-lg mb-3 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#F5D313] inline-block shadow-[0_0_0_3px_rgba(245,211,19,0.35)]" />
                  {o.label}
                </h3>
                <ul className="space-y-1.5 text-[11px] uppercase tracking-wide text-slate-300/90">
                  {o.points.map(pt => (
                    <li key={pt} className="flex items-start gap-1">
                      <span className="text-[#F5D313] mt-[2px]">•</span>
                      <span className="tracking-wide">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pointer-events-none absolute -inset-px rounded-3xl ring-0 group-hover:ring-2 ring-[#F5D313]/40 transition" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Keyframe for entry animation
// (Tailwind doesn't generate dynamic keyframes declared inline—inject via global style approach if needed in a layout/global CSS.)
